import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
  FormHelperText
} from '@mui/material';
import {
  Plus,
  Pencil,
  Trash,
  Clock,
  MapPin,
  Users,
  Calendar,
  LinkSimple,
  Copy,
  Code
} from '@phosphor-icons/react';
import { useBusiness } from '../../context/BusinessContext';
import { useBusinessSubscription } from '../../hooks/useBusinessSubscription';
import { usePermissions, PermissionGate } from '../../hooks/usePermissions';
import { useNavigate } from 'react-router-dom';
import PermissionDeniedBanner from '../Common/PermissionDeniedBanner';
import UpgradeCTA from '../Business/UpgradeCTA';
import { getPermissionMessage } from '../../utils/permissionMessages';
import api from '../../utils/api';

function normaliseDomainLine(d) {
  return String(d).trim().toLowerCase();
}

function computeManualDomainLines(publicEmbedAllowedDomains, publicEmbedDerivedDomains) {
  const dset = new Set((publicEmbedDerivedDomains || []).map(normaliseDomainLine));
  return (publicEmbedAllowedDomains || [])
    .map(normaliseDomainLine)
    .filter(Boolean)
    .filter((x) => !dset.has(x))
    .join('\n');
}

const ServiceTypeManagement = () => {
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();
  const { planKey, loading: subscriptionLoading } = useBusinessSubscription();
  const { _can } = usePermissions();
  const isFreePlan = planKey === 'supplier_free' || planKey === 'supplier_free_trial';
  const canUsePublicEmbed =
    planKey === 'supplier_individual'
    || planKey === 'supplier_team'
    || planKey === 'supplier_enterprise';
  const [serviceTypes, setServiceTypes] = useState([]);
  const atAppointmentTypeLimit = isFreePlan && serviceTypes.length >= 1;
  const canAddAppointmentType = !atAppointmentTypeLimit;
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showEmbedDialog, setShowEmbedDialog] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState(null);
  const [embedServiceType, setEmbedServiceType] = useState(null);
  const businessId = selectedBusiness?.business?._id || selectedBusiness?._id || '';

  const normaliseMemberId = (member) => {
    if (!member) return '';
    if (typeof member === 'string') return member;
    if (typeof member === 'object' && member._id) return String(member._id);
    if (member.toString) return member.toString();
    return '';
  };

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#1976d2',
    duration: 60,
    isBlockerService: false,
    bookingGap: 0,
    locationType: '',
    locationAddress: '',
    // pricing: {
    //   basePrice: null,
    //   currency: 'GBP',
    //   pricingModel: 'fixed',
    //   depositPercentage: 0
    // },
    requiredTeamMembers: 1,
    allowedTeamMembers: [],
    requiresApproval: true,
    allowRescheduling: true,
    allowCancellation: true,
    isActive: true
  });

  const [publicEmbedEnabled, setPublicEmbedEnabled] = useState(false);
  const [publicEmbedExtraDomains, setPublicEmbedExtraDomains] = useState('');
  const [publicEmbedToken, setPublicEmbedToken] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);

  useEffect(() => {
    if (businessId) {
      fetchServiceTypes();
      fetchTeamMembers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId]);

  const loadPublicEmbedSettings = async (serviceTypeId) => {
    try {
      const res = await api.get(`/api/service-types/${serviceTypeId}`);
      setPublicEmbedEnabled(Boolean(res.data.publicEmbedEnabled));
      setPublicEmbedExtraDomains(
        computeManualDomainLines(res.data.publicEmbedAllowedDomains, res.data.publicEmbedDerivedDomains)
      );
      setPublicEmbedToken(res.data.publicEmbedToken || '');
    } catch {
      setPublicEmbedEnabled(false);
      setPublicEmbedExtraDomains('');
      setPublicEmbedToken('');
    }
  };

  const fetchServiceTypes = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/service-types', {
        params: { businessId }
      });
      setServiceTypes(response.data.serviceTypes);
    } catch (error) {
      setError('Failed to load service types');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    if (!businessId) {
      setTeamMembers([]);
      return;
    }

    try {
      const response = await api.get(`/api/supplier/staff-members?businessId=${businessId}`);
      const staffMembers = response.data.staffMembers || [];
      
      // Transform staff members to match expected format
      const members = staffMembers
        .filter(sm => sm.status === 'active')
        .map(sm => {
          const displayName = sm.user 
            ? `${sm.user.firstName || ''} ${sm.user.lastName || ''}`.trim() || sm.name
            : sm.name;
          // Allowed team members should map to Schedules staff members.
          const businessMemberId = normaliseMemberId(sm.businessMember?._id || sm.businessMember);
          const staffMemberId = normaliseMemberId(sm._id);
          
          return {
            _id: staffMemberId,
            staffMemberId,
            businessMemberId,
            user: sm.user || {
              firstName: sm.name.split(' ')[0] || '',
              lastName: sm.name.split(' ').slice(1).join(' ') || ''
            },
            role: sm.role || 'Staff',
            displayName
          };
        });
      
      setTeamMembers(members);
    } catch (error) {
      setTeamMembers([]);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'allowedTeamMembers') {
      const staffIds = new Set(
        teamMembers.map(member => normaliseMemberId(member._id)).filter(Boolean)
      );

      const cleanedValues = (Array.isArray(value) ? value : [value])
        .map(normaliseMemberId)
        .filter(memberId => staffIds.has(memberId));

      setFormData(prev => ({
        ...prev,
        [field]: cleanedValues
      }));
      return;
    }

    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    if (formSubmitAttempted) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateAppointmentForm = () => {
    const errors = {};
    if (!String(formData.name || '').trim()) {
      errors.name = 'Add a name for this appointment type';
    }
    const duration = Number(formData.duration);
    if (!Number.isFinite(duration) || duration < 1) {
      errors.duration = 'Enter how long it lasts (at least 1 minute)';
    }
    if (!formData.locationType) {
      errors.locationType = 'Choose where this appointment usually takes place';
    }
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateAppointmentForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setFormSubmitAttempted(true);
      setError('Please fill in the required fields highlighted below.');
      return;
    }

    if (!editingServiceType) {
      if (subscriptionLoading) {
        setError('Still checking your plan. Please try again in a moment.');
        return;
      }
      if (atAppointmentTypeLimit) {
        setError('Your plan allows 1 appointment type only. Upgrade to add more.');
        return;
      }
    }

    setFormErrors({});
    setFormSubmitAttempted(false);
    setLoading(true);
    setError('');

    try {
      const activeTeamMemberIds = new Set(
        teamMembers.map(member => normaliseMemberId(member._id)).filter(Boolean)
      );

      const payload = {
        businessId,
        ...formData,
        allowedTeamMembers: (formData.allowedTeamMembers || [])
          .map(normaliseMemberId)
          .filter(memberId => activeTeamMemberIds.has(memberId))
          .filter(Boolean)
      };

      if (editingServiceType) {
        await api.put(`/api/service-types/${editingServiceType._id}`, payload);
        setSuccess('Service type updated successfully');
      } else {
        await api.post('/api/service-types', payload);
        setSuccess('Service type created successfully');
      }

      setShowForm(false);
      setEditingServiceType(null);
      resetForm();
      fetchServiceTypes();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save service type');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (serviceType) => {
    const allowedTeamMembers = (serviceType.allowedTeamMembers || [])
      .map((memberId) => {
        const normalisedId = normaliseMemberId(memberId);
        const matchedTeamMember = teamMembers.find((member) =>
          normaliseMemberId(member._id) === normalisedId ||
          normaliseMemberId(member.businessMemberId) === normalisedId
        );
        return matchedTeamMember ? normaliseMemberId(matchedTeamMember._id) : normalisedId;
      })
      .filter(Boolean);

    setFormData({
      name: serviceType.name,
      description: serviceType.description || '',
      color: serviceType.color || '#1976d2',
      duration: serviceType.duration,
      isBlockerService: serviceType.isBlockerService,
      bookingGap: serviceType.bookingGap,
      locationType: serviceType.locationType || '',
      locationAddress: serviceType.locationAddress || '',
      // pricing: {
      //   basePrice: serviceType.pricing?.basePrice || null,
      //   currency: serviceType.pricing?.currency || 'GBP',
      //   pricingModel: serviceType.pricing?.pricingModel || 'fixed',
      //   depositPercentage: serviceType.pricing?.depositPercentage || 0
      // },
      requiredTeamMembers: serviceType.requiredTeamMembers,
      allowedTeamMembers,
      requiresApproval: serviceType.requiresApproval,
      allowRescheduling: serviceType.allowRescheduling,
      allowCancellation: serviceType.allowCancellation,
      isActive: serviceType.isActive
    });
    setEditingServiceType(serviceType);
    setFormErrors({});
    setFormSubmitAttempted(false);
    setShowForm(true);
  };

  const handleOpenAddForm = () => {
    if (subscriptionLoading) {
      setError('Still checking your plan. Please try again in a moment.');
      return;
    }
    if (atAppointmentTypeLimit) {
      setError('Your plan allows 1 appointment type only. Upgrade to add more.');
      return;
    }
    setEditingServiceType(null);
    resetForm();
    setError('');
    setShowForm(true);
  };

  const handleOpenEmbed = (serviceType) => {
    setEmbedServiceType(serviceType);
    setShowEmbedDialog(true);
    if (canUsePublicEmbed) {
      loadPublicEmbedSettings(serviceType._id);
    }
  };

  const handleCloseEmbedDialog = () => {
    setShowEmbedDialog(false);
    setEmbedServiceType(null);
    setPublicEmbedEnabled(false);
    setPublicEmbedExtraDomains('');
    setPublicEmbedToken('');
  };

  const handleSaveEmbed = async () => {
    if (!embedServiceType?._id || !canUsePublicEmbed) return;
    setLoading(true);
    setError('');
    try {
      await api.put(`/api/service-types/${embedServiceType._id}`, {
        publicEmbedEnabled,
        publicEmbedExtraDomains: publicEmbedExtraDomains
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean)
      });
      const res = await api.get(`/api/service-types/${embedServiceType._id}`);
      setPublicEmbedToken(res.data.publicEmbedToken || '');
      setSuccess('Website booking settings saved');
      fetchServiceTypes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save website booking settings');
    } finally {
      setLoading(false);
    }
  };

  const handleRotatePublicEmbedToken = async () => {
    if (!embedServiceType?._id) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.post(`/api/service-types/${embedServiceType._id}/public-embed/rotate-token`);
      setPublicEmbedToken(res.data.publicEmbedToken || '');
      setSuccess('Public booking link updated. Update your website embed if you use it.');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to rotate link');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (serviceTypeId) => {
    if (!window.confirm('Are you sure you want to delete this service type?')) {
      return;
    }

    try {
      await api.delete(`/api/service-types/${serviceTypeId}`);
      setSuccess('Service type deleted successfully');
      fetchServiceTypes();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete service type');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#1976d2',
      duration: 60,
      isBlockerService: false,
      bookingGap: 0,
      locationType: '',
      locationAddress: '',
      // pricing: {
      //   basePrice: null,
      //   currency: 'GBP',
      //   pricingModel: 'fixed',
      //   depositPercentage: 0
      // },
      requiredTeamMembers: 1,
      allowedTeamMembers: [],
      requiresApproval: true,
      allowRescheduling: true,
      allowCancellation: true,
      isActive: true
    });
    setPublicEmbedEnabled(false);
    setPublicEmbedExtraDomains('');
    setPublicEmbedToken('');
    setFormErrors({});
    setFormSubmitAttempted(false);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingServiceType(null);
    resetForm();
  };

  const handleRemoveAllowedTeamMember = (memberIdToRemove) => {
    const normalisedId = normaliseMemberId(memberIdToRemove);
    setFormData(prev => ({
      ...prev,
      allowedTeamMembers: (prev.allowedTeamMembers || []).filter(
        memberId => normaliseMemberId(memberId) !== normalisedId
      )
    }));
  };

  if (!selectedBusiness) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info">
          Please select a business to manage service types.
        </Alert>
      </Container>
    );
  }

  return (
    <PermissionGate 
      permission="manage_services"
      fallback={
        <PermissionDeniedBanner
          {...getPermissionMessage('SERVICE_TYPES')}
          backButtonPath="/supplier/bookings"
        />
      }
    >
      <Container maxWidth="xl" sx={{ py: 4, pt: 0, px: 0 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 3,
          gap: 1.5,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
        }}
      >
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Calendar size={24} />
          Appointment Types
        </Typography>
        <PermissionGate permission="manage_services">
          {canAddAppointmentType && (
            <Button
              variant="contained"
              startIcon={<Plus />}
              onClick={handleOpenAddForm}
              disabled={subscriptionLoading}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Add Appointment Type
            </Button>
          )}
        </PermissionGate>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {serviceTypes.map((serviceType) => (
            <Grid item xs={12} sm={6} md={4} key={serviceType._id}>
              <Card sx={{
                height: '100%',
                border: '1px solid #e0e0e0 !important',
                boxShadow: 'none'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      {serviceType.name || 'Unnamed Service'}
                    </Typography>
                    <PermissionGate permission="manage_services">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip
                          title={
                            canUsePublicEmbed
                              ? 'Website embed and booking link'
                              : 'Upgrade to embed booking on your website'
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEmbed(serviceType)}
                            aria-label="Website embed and booking link"
                          >
                            <Code size={16} weight="regular" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(serviceType)}
                          aria-label="Edit appointment type"
                        >
                          <Pencil size={16} weight="regular" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(serviceType._id)}
                          color="error"
                          aria-label="Delete appointment type"
                        >
                          <Trash size={16} />
                        </IconButton>
                      </Box>
                    </PermissionGate>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {serviceType.description || 'No description provided'}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: serviceType.color || '#1976d2',
                        border: '1px solid #ddd'
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Appointment colour
                    </Typography>
                  </Box>

                  <List dense>
                    <ListItem sx={{ px: 0 }}>
                      <Clock size={16} style={{ marginRight: 8 }} />
                      <ListItemText 
                        primary={`${serviceType.duration || 0} minutes`}
                        secondary="Duration"
                      />
                    </ListItem>

                    <ListItem sx={{ px: 0 }}>
                      <MapPin size={16} weight="regular" style={{ marginRight: 8 }} />
                      <ListItemText 
                        primary={serviceType.locationType === 'on_site' ? 'On-site' : 
                                serviceType.locationType === 'off_site' ? 'Off-site' : 
                                serviceType.locationType === 'virtual' ? 'Virtual' : (serviceType.locationType || 'Not specified')}
                        secondary={serviceType.locationAddress || 'Location Type'}
                      />
                    </ListItem>

                    <ListItem sx={{ px: 0 }}>
                      <Users size={16} style={{ marginRight: 8 }} />
                      <ListItemText 
                        primary={`${serviceType.requiredTeamMembers ?? 1} team member${(serviceType.requiredTeamMembers ?? 1) > 1 ? 's' : ''} required`}
                        secondary={serviceType.allowedTeamMembers && Array.isArray(serviceType.allowedTeamMembers) && serviceType.allowedTeamMembers.length > 0 
                          ? `${serviceType.allowedTeamMembers.length} allowed team members`
                          : 'All team members allowed'
                        }
                      />
                    </ListItem>

                    {/* Pricing removed for now */}
                  </List>

                  <Box sx={{ mt: 2 }}>
                    {serviceType.isBlockerService && (
                      <Chip label="Blocker Service" size="small" color="warning" sx={{ mr: 1, mb: 1 }} />
                    )}
                    {!serviceType.isActive && (
                      <Chip label="Inactive" size="small" color="default" sx={{ mr: 1, mb: 1 }} />
                    )}
                    {serviceType.requiresApproval && (
                      <Chip label="Requires Approval" size="small" color="info" sx={{ mr: 1, mb: 1 }} />
                    )}
                    {canUsePublicEmbed && serviceType.publicEmbedEnabled && serviceType.publicEmbedTokenConfigured && (
                      <Chip label="Public booking link on" size="small" color="success" sx={{ mr: 1, mb: 1 }} />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {atAppointmentTypeLimit && (
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{
                height: '100%',
                border: '1px solid #e0e0e0 !important',
                boxShadow: 'none'
              }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                  <UpgradeCTA reason="more appointment types" compact />
                </CardContent>
              </Card>
            </Grid>
          )}

          {serviceTypes.length === 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    No Service Types
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create your first service type to start accepting bookings.
                  </Typography>
                  <PermissionGate permission="manage_services">
                    {canAddAppointmentType ? (
                      <Button
                        variant="contained"
                        startIcon={<Plus />}
                        onClick={handleOpenAddForm}
                        disabled={subscriptionLoading}
                      >
                        Add Appointment Type
                      </Button>
                    ) : (
                      <UpgradeCTA reason="more appointment types" compact />
                    )}
                  </PermissionGate>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Service Type Form Dialog */}
      <Dialog
        open={showForm}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingServiceType ? 'Edit Appointment Type' : 'Add Appointment Type'}
        </DialogTitle>
        
        <DialogContent>
          {formSubmitAttempted && Object.keys(formErrors).length > 0 && (
            <Alert severity="error" sx={{ mt: 1, mb: 0 }}>
              Please complete the required fields: name, duration, and where the appointment takes place.
            </Alert>
          )}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Service name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                error={Boolean(formErrors.name)}
                helperText={
                  formErrors.name
                    || 'The name of your service (e.g. Wedding photography, DJ set)'
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                value={formData.duration}
                onChange={(e) => handleInputChange('duration', parseInt(e.target.value, 10))}
                required
                error={Boolean(formErrors.duration)}
                helperText={
                  formErrors.duration
                    || 'How long this service typically takes (e.g. 60 for 1 hour, 120 for 2 hours)'
                }
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                helperText="Optional description of what this service includes"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Colour"
                type="color"
                value={formData.color}
                onChange={(e) => handleInputChange('color', e.target.value)}
                helperText="Used on appointment cards in your calendar"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Booking Gap (minutes)"
                type="number"
                value={formData.bookingGap}
                onChange={(e) => handleInputChange('bookingGap', parseInt(e.target.value))}
                helperText="Time buffer between bookings (e.g., 30 minutes for setup/cleanup)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                required
                error={Boolean(formErrors.locationType)}
              >
                <InputLabel id="appointment-location-type-label" required>
                  Where does this take place?
                </InputLabel>
                <Select
                  labelId="appointment-location-type-label"
                  label="Where does this take place?"
                  value={formData.locationType}
                  onChange={(e) => handleInputChange('locationType', e.target.value)}
                >
                  <MenuItem value="" disabled>
                    <em>Select location type</em>
                  </MenuItem>
                  <MenuItem value="on_site">On-site (at your venue or studio)</MenuItem>
                  <MenuItem value="off_site">Off-site (at the couple's venue)</MenuItem>
                  <MenuItem value="virtual">Virtual (video call)</MenuItem>
                </Select>
                <FormHelperText error={Boolean(formErrors.locationType)}>
                  {formErrors.locationType
                    || 'Required. Choose where couples usually meet you for this appointment.'}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Conditional Location Address Field */}
            {(formData.locationType === 'on_site' || formData.locationType === 'off_site') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location Address"
                  value={formData.locationAddress}
                  onChange={(e) => handleInputChange('locationAddress', e.target.value)}
                  helperText={formData.locationType === 'on_site' 
                    ? "The address where you'll provide the service (e.g., your studio, office)"
                    : "The address where the service will take place (e.g., client's venue, event location)"
                  }
                />
              </Grid>
            )}

            {formData.locationType === 'virtual' && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Virtual Service:</strong> A video call link will be sent to the client when the booking is confirmed.
                  </Typography>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Required Team Members"
                type="number"
                value={formData.requiredTeamMembers}
                onChange={(e) => handleInputChange('requiredTeamMembers', parseInt(e.target.value))}
                inputProps={{ min: 1 }}
                helperText="Minimum number of team members needed for this service"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Allowed Team Members</InputLabel>
                <Select
                  multiple
                  value={formData.allowedTeamMembers}
                  onChange={(e) => handleInputChange('allowedTeamMembers', e.target.value)}
                  label="Allowed Team Members"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const valueId = normaliseMemberId(value);
                        const member = teamMembers.find(m => normaliseMemberId(m._id) === valueId);
                        const fallbackLabel = valueId ? `Removed team member (${valueId})` : 'Removed team member';
                        return (
                          <Chip 
                            key={valueId || String(value)}
                            label={member ? `${member.user?.firstName} ${member.user?.lastName}` : fallbackLabel}
                            size="small"
                            onDelete={() => handleRemoveAllowedTeamMember(valueId)}
                            deleteIcon={
                              <span
                                onMouseDown={(event) => event.stopPropagation()}
                                style={{ fontSize: 14, lineHeight: 1 }}
                              >
                                x
                              </span>
                            }
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {teamMembers.map((member) => (
                    <MenuItem
                      key={member.staffMemberId || normaliseMemberId(member._id) || member.displayName}
                      value={normaliseMemberId(member._id)}
                    >
                      {member.user?.firstName} {member.user?.lastName} ({member.role})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {teamMembers.length > 0
                    ? 'Select which team members can handle this service type. Leave empty to allow all team members.'
                    : 'No team members have schedules yet. Add schedules to choose team members for this appointment type.'}
                </FormHelperText>
                {teamMembers.length === 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => navigate('/supplier/schedules')}
                    >
                      Add Schedules
                    </Button>
                  </Box>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Settings
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isBlockerService}
                    onChange={(e) => handleInputChange('isBlockerService', e.target.checked)}
                  />
                }
                label={
                  <Tooltip title="A blocker service prevents other bookings during this time (e.g., full-day events)">
                    <span>Blocker Service</span>
                  </Tooltip>
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requiresApproval}
                    onChange={(e) => handleInputChange('requiresApproval', e.target.checked)}
                  />
                }
                label={
                  <Tooltip title="If enabled, bookings for this service need manual approval before confirmation">
                    <span>Requires Approval</span>
                  </Tooltip>
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allowRescheduling}
                    onChange={(e) => handleInputChange('allowRescheduling', e.target.checked)}
                  />
                }
                label={
                  <Tooltip title="Allow customers to reschedule bookings for this service">
                    <span>Allow Rescheduling</span>
                  </Tooltip>
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allowCancellation}
                    onChange={(e) => handleInputChange('allowCancellation', e.target.checked)}
                  />
                }
                label={
                  <Tooltip title="Allow customers to cancel bookings for this service">
                    <span>Allow Cancellation</span>
                  </Tooltip>
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  />
                }
                label={
                  <Tooltip title="Only active services can be booked by customers">
                    <span>Active</span>
                  </Tooltip>
                }
              />
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseForm}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : (editingServiceType ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showEmbedDialog}
        onClose={handleCloseEmbedDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkSimple size={22} />
          Website booking
          {embedServiceType?.name ? ` · ${embedServiceType.name}` : ''}
        </DialogTitle>
        <DialogContent>
          {!canUsePublicEmbed ? (
            <UpgradeCTA reason="website booking embed" />
          ) : (
          <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share a link or paste an iframe on your site. Couples pick a date and time, then send a request, like booking in the app.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={publicEmbedEnabled}
                onChange={(e) => setPublicEmbedEnabled(e.target.checked)}
              />
            }
            label="Turn on public booking page"
          />
          {publicEmbedEnabled && !String(selectedBusiness?.business?.contactInfo?.website || '').trim() ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Add a website on your business profile so we can lock embedding to your domain. Until then, the booking page is not restricted by host (same as sharing a public link).
            </Alert>
          ) : null}
          {publicEmbedToken ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Public link
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={`${typeof window !== 'undefined' ? window.location.origin : ''}/book/${publicEmbedToken}`}
                InputProps={{ readOnly: true }}
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Copy size={18} />}
                  onClick={() => {
                    const url = `${window.location.origin}/book/${publicEmbedToken}`;
                    navigator.clipboard.writeText(url).then(() => setSuccess('Link copied')).catch(() => setError('Could not copy'));
                  }}
                >
                  Copy link
                </Button>
                <Button size="small" variant="outlined" onClick={handleRotatePublicEmbedToken} disabled={loading}>
                  Regenerate link
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Embed code (iframe)
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={`<iframe src="${typeof window !== 'undefined' ? window.location.origin : ''}/book/${publicEmbedToken}" width="100%" height="900" style="border:0" title="Book an appointment"></iframe>`}
                InputProps={{ readOnly: true }}
                sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<Copy size={18} />}
                sx={{ mt: 1 }}
                onClick={() => {
                  const code = `<iframe src="${window.location.origin}/book/${publicEmbedToken}" width="100%" height="900" style="border:0" title="Book an appointment"></iframe>`;
                  navigator.clipboard.writeText(code).then(() => setSuccess('Embed code copied')).catch(() => setError('Could not copy'));
                }}
              >
                Copy embed code
              </Button>
            </Box>
          ) : publicEmbedEnabled ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Save to generate your booking link and embed code.
            </Alert>
          ) : null}
          <Box
            sx={{
              mt: 2.5,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" component="p" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
              Extra domains (optional)
            </Typography>
            <Typography variant="caption" component="p" color="text.secondary" sx={{ mb: 1, lineHeight: 1.4 }}>
              Your business website is used automatically. Add lines here only if the iframe sits on another website (e.g. bookings.example.co.uk). One domain per line; wildcards like *.example.co.uk are supported.
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={1}
              maxRows={3}
              value={publicEmbedExtraDomains}
              onChange={(e) => setPublicEmbedExtraDomains(e.target.value)}
              placeholder="bookings.example.co.uk"
              inputProps={{ style: { fontSize: '0.8125rem' } }}
            />
          </Box>
          </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmbedDialog}>{canUsePublicEmbed ? 'Cancel' : 'Close'}</Button>
          {canUsePublicEmbed ? (
            <Button variant="contained" onClick={handleSaveEmbed} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Container>
    </PermissionGate>
  );
};

export default ServiceTypeManagement;
